import { Component, Input, OnDestroy, OnInit, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ShareValuesService } from 'src/app/services/sharedValues/share-values.service';
import { ConfirmationService } from 'src/app/services/confirmation.service';
import { AppConstants } from 'src/app/utilities/constant';
import { forkJoin, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { BackendCallService } from 'src/app/services/backend-call/backend-call.service';


@Component({
  selector: 'app-initial-hazard-measure',
  templateUrl: './initial-hazard-measure.component.html',
  styleUrls: ['./initial-hazard-measure.component.scss']
})
export class InitialHazardMeasureComponent implements OnInit {
  @Input() roadmapStatus: string;
  @Input() isCustom: string;
  @Input() isRiskReassessment: boolean;
  @Input() addNewHazard: boolean;
  @Input() hardDetailsData: any;
  @Input() pickInitialHazardValue: string;
  @Input() pickCntrlMeasureValue: string;
  @Input() measureTypes: any[];
  @Input() associatedMachineModes: any;
  @Input() plrObj: any;
  @Input() recommendedCategoryValue: string;
  @Input() isIncludeCategoryChecked: boolean = false;
  @Input() existingControlMeasures: any[];
  @Input() isReadonly: boolean = false;
  @Input() showValidationErrors: boolean = false;
  @Input() serviceMachineId: string;
  @Output() openPickMeasure = new EventEmitter<{ type: string, index?: number, measure:any }>();
  @Output() initialMeasureAddToLibrary = new EventEmitter<string>();
  @Output() determinePlr = new EventEmitter<any>();
  @Output() controlMeasureAddToLibrary = new EventEmitter<any>();
  @Output() addCategoryValue = new EventEmitter<void>();
  @Output() measuresUpdated = new EventEmitter<any[]>();
  private controlMeasureDebounceMap: Map<string, Subject<string>> = new Map();
  private measureNameValidationSubject = new Subject<void>();
  public  dynamicMeasures: {
    measure_id: string;
    measure_type_id: string;
    measure_type_name: string;
    selectedMeasureType?: any;
    short_name?: string;
    showMeasureTypeRequiredError?: boolean;
    showWhitespaceOnlyShortNameError?: boolean;
    showDuplicateShortNameError?: boolean;
    control_measure: string;
    order_no: number;
    machineModes: any;
    is_plr: boolean;
    measurePlrObj?: {
    plr: string;
    sValue: string;
    pValue: string;
    fValue: string;
    category: string;
    isIncludeRecommandedCategory: boolean;
    recommendedCategory: string;
    plrDescription: string;
    isPlrFromGraph : boolean; 
    };
    _nameItems?: string[];
    _nameLoading?: boolean;
    _nameIncreasedCount?: number;
    _nameHasMore?: boolean;
    _currentSearch?: string;
    _nameSearchTimer?: any;
    _nameSub?: any;
  }[] = [];
  isFormValid: boolean = false;
  selectedMeasureType: string;
  selectedMeasureIndex: number | null = null;
  public defaultMeasureDescLength = 1500;
  public measureDescMaxLength = 1501;
  public readonly measureNameMaxLength = 30;
  public removeLabel: string = '';
  private langChangeSub: any;

  constructor(private translate: TranslateService, public sharedValue: ShareValuesService, private confirmationService: ConfirmationService, private backendService: BackendCallService) {}


  ngOnInit() {
    this.removeLabel = this.translate.instant('Remove');
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.removeLabel = this.translate.instant('Remove');
      this.measureTypes = this.measureTypes.map(type => {
        type.label = this.translateMeasureTypeName(type.measure_type_name);
        return type;
      });
    });
    this.measureNameValidationSubject.pipe(debounceTime(600)).subscribe(() => {
      this.validateDuplicateMeasureNames();
      this.emitUpdatedMeasures();
    });

    this.measureTypes = this.sharedValue.measureTypes.map(type => ({
      ...type,
      label: this.translateMeasureTypeName(type.measure_type_name)
    }));
    if (this.existingControlMeasures?.length) {
      this.dynamicMeasures = this.existingControlMeasures.map((measure, index) => {
        const selectedType = this.measureTypes.find(
          type => type.measure_type_id === measure.measure_type_id
        );
        if (measure.measure_type_id === 3) {
          if(!measure.measurePlrObj) {
            measure.is_plr = true;
            measure.measurePlrObj = {
              plr: '',
              sValue: '',
              pValue: '',
              fValue: '',
              category: '',
              isIncludeRecommandedCategory: true,
              recommendedCategory: '',
              plrDescription : '',
              isPlrFromGraph : false
            };
          } else {
            if (measure.measurePlrObj.isIncludeRecommandedCategory !== false) {
              measure.measurePlrObj.isIncludeRecommandedCategory = true;
            }
            if(measure?.measurePlrObj?.category)  measure.measurePlrObj.recommendedCategory = measure.measurePlrObj.category;
          }
        }
        return {
          ...measure,
          order_no: index + 1,
          short_name: measure.short_name || '',
          selectedMeasureType: selectedType || null
        };
      });
      this.emitUpdatedMeasures();
    } else {
      const newOrderNo = 1;
      this.dynamicMeasures = [{
        measure_id: '',
        measure_type_id: '',
        measure_type_name: '',
        short_name: '',
        control_measure: this.hardDetailsData.pickControlMeasure,
        order_no: newOrderNo,
        is_plr: false,
        machineModes: [...this.associatedMachineModes],
      }];
    }
  }

  private translateMeasureTypeName(name: string): string {
    const label = String(name || '').trim();
    const normalizedKey = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    for (const key of [label, normalizedKey]) {
      const translated = key ? this.translate.instant(key) : '';
      if (translated && translated !== key) {
        return translated;
      }
    }
    return label;
  }

  ngOnDestroy() {
    this.measureNameValidationSubject.complete();
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
    this.dynamicMeasures.forEach(measure => {
      if (measure._nameSub) { measure._nameSub.unsubscribe(); }
      clearTimeout(measure._nameSearchTimer);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.showValidationErrors) {
      this.updateRequiredFieldErrors();
    }
    if (changes.pickCntrlMeasureValue && this.selectedMeasureIndex !== null) {
      const selectedValue = changes.pickCntrlMeasureValue.currentValue;
      this.pickCntrlMeasureValue = selectedValue;
      this.dynamicMeasures[this.selectedMeasureIndex].control_measure = selectedValue;
      // this.checkFormValidity();
    }
    if(changes.plrObj && this.selectedMeasureIndex !== null) {
      this.dynamicMeasures[this.selectedMeasureIndex].is_plr = true;
      this.dynamicMeasures[this.selectedMeasureIndex].measurePlrObj = changes.plrObj.currentValue
      this.emitUpdatedMeasures();
    }
    if(changes.associatedMachineModes) {
      const previousMachineModes = changes.associatedMachineModes.previousValue || [];
      this.associatedMachineModes = changes.associatedMachineModes.currentValue || [];
      if (!changes.associatedMachineModes.firstChange && this.dynamicMeasures.length) {
        const previousModeValues = new Set(
          previousMachineModes
            .map(mode => this.getModeValue(mode))
            .filter(modeValue => modeValue.length > 0)
        );
        const newlyAvailableModes = this.associatedMachineModes.filter(mode => {
          const modeValue = this.getModeValue(mode);
          return modeValue && !previousModeValues.has(modeValue);
        });
        this.dynamicMeasures = this.dynamicMeasures.map(measure => ({
          ...measure,
          machineModes: this.getRetainedMachineModes(measure?.machineModes, newlyAvailableModes)
        }));
        this.emitUpdatedMeasures();
      }
    }
  }

  transformMachineModes(modes: { value: string }[]): void {
    const modeObservables = modes?.map(item => this.translate.get(item.value));
    forkJoin(modeObservables).subscribe(translations => {
      this.associatedMachineModes = modes.map((item, index) => ({
        value: item.value,
        label: translations[index]
      }));
    });
  }

  compareModeObjects(o1: any, o2: any): boolean {
    return o1?.value === o2?.value;
  }

  private getModeValue(mode: any): string {
    if (typeof mode === 'string') {
      return mode;
    }

    return String(mode?.value || mode?.mode_name || mode?.machineMode || mode?.name || '');
  }

  private getRetainedMachineModes(machineModes: any, modesToAdd: any[] = []): any[] {
    const currentModes = Array.isArray(machineModes) ? machineModes : machineModes ? [machineModes] : [];
    const availableModeValues = new Set(
      (this.associatedMachineModes || [])
        .map(mode => this.getModeValue(mode))
        .filter(modeValue => modeValue.length > 0)
    );

    if (!availableModeValues.size) {
      return [];
    }

    const retainedMachineModes = currentModes.filter(mode => availableModeValues.has(this.getModeValue(mode)));
    modesToAdd.forEach(mode => {
      const modeValue = this.getModeValue(mode);
      const shouldAddMode = modeValue &&
        availableModeValues.has(modeValue) &&
        !retainedMachineModes.some(retainedMode => this.getModeValue(retainedMode) === modeValue);
      if (shouldAddMode) {
        retainedMachineModes.push(mode);
      }
    });

    return retainedMachineModes;
  }


  public changeMode(selectedModes, index) {
    this.selectedMeasureIndex = index;
    if (this.dynamicMeasures[index]) {
      this.dynamicMeasures[index].machineModes = selectedModes;
      console.log(`Updated machineModes for measure at index ${index}:`, selectedModes);
      // this.checkFormValidity();
      this.emitUpdatedMeasures();
    }
  }

  onChangeMeasureType(selectedType: any, index: number) {
    const measure = this.dynamicMeasures[index];
    const retainedMachineModes = this.getRetainedMachineModes(measure?.machineModes);
    measure.showMeasureTypeRequiredError = false;
    if (!selectedType) {
      measure.measure_type_id = '';
      measure.measure_type_name = '';
      measure.machineModes = retainedMachineModes;
      delete measure.measurePlrObj;
      this.emitUpdatedMeasures();
      // this.checkFormValidity();
      return;
    }
    measure.measure_type_id = selectedType.measure_type_id;
    measure.measure_type_name = selectedType.measure_type_name;
    measure.machineModes = retainedMachineModes;
    if (selectedType.measure_type_id === 3) {
      if(!measure.measurePlrObj) {
        measure.is_plr = true;
        measure.measurePlrObj = {
          plr: '',
          sValue: '',
          pValue: '',
          fValue: '',
          category: '',
          isIncludeRecommandedCategory: true,
          recommendedCategory: '',
           plrDescription: '',
          isPlrFromGraph : false 
        };
      } else {
       if (measure.measurePlrObj.isIncludeRecommandedCategory !== false) {
              measure.measurePlrObj.isIncludeRecommandedCategory = true;
            }
        if(measure?.measurePlrObj?.category) measure.measurePlrObj.recommendedCategory = measure.measurePlrObj.category;
      }
    } else {
      measure.measurePlrObj = null;
      measure.is_plr = false;
    }
    // this.checkFormValidity();
    this.emitUpdatedMeasures();
  }

  handleOpenPickMeasure(type: string, index: number, measure) {
    this.selectedMeasureIndex = index;
    const measureType = measure?.selectedMeasureType;
    this.openPickMeasure.emit({ type, index, measure });
    this.emitUpdatedMeasures();
  }

  handleAddToLibrary(value: string) {
    this.initialMeasureAddToLibrary.emit(value);
  }

  handleControlMeasureAddToLibrary(measure, descriptionRef: HTMLIonTextareaElement) {
    const measureValue = descriptionRef.value;
    measure.control_measure = measureValue;
    this.controlMeasureAddToLibrary.emit(measure);
    setTimeout(() => {
      this.emitUpdatedMeasures();
      console.log('after add dynamicmeasure:', this.dynamicMeasures);
    });
  }

  onControlMeasureChanged(measure, descriptionRef: HTMLIonTextareaElement, i: number): void {
    const measureValue = descriptionRef.value;
    measure.control_measure = measureValue;
    const index = i.toString();
    if (measure.control_measure && measure.control_measure.length >= this.measureDescMaxLength) {
      measure.control_measure = measure.control_measure.substring(0, this.measureDescMaxLength);
    }
    if (!this.controlMeasureDebounceMap.has(index)) {
      const subject = new Subject<string>();
      subject.pipe(debounceTime(600)).subscribe(() => {
        this.emitUpdatedMeasures();
      });
      this.controlMeasureDebounceMap.set(index, subject);
    }
    this.controlMeasureDebounceMap.get(index)?.next(measureValue);
  }

  addMeasureNameTag = (term: string) => term?.substring(0, this.measureNameMaxLength) || null;

  onMeasureNameOpen(measure: any): void {
    this.loadShortNames(measure, measure._currentSearch || '', true);
  }

  onMeasureNameSearch(measure: any, event: { term: string }): void {
    clearTimeout(measure._nameSearchTimer);
    const term = event?.term || '';
    measure._nameSearchTimer = setTimeout(() => {
      this.loadShortNames(measure, term, true);
    }, 300);
  }

  onMeasureNameScrollToEnd(measure: any): void {
    if (!measure._nameLoading && measure._nameHasMore) {
      this.loadShortNames(measure, measure._currentSearch || '', false);
    }
  }

  private loadShortNames(measure: any, search: string, reset: boolean): void {
    if (!this.serviceMachineId) { return; }
    if (reset) {
      measure._nameIncreasedCount = 0;
      measure._currentSearch = search;
    }
    const count = 20;
    const payload = {
      serviceMachineId: this.serviceMachineId,
      count,
      increasedCount: measure._nameIncreasedCount || 0,
      search: search || ''
    };
    measure._nameLoading = true;
    if (measure._nameSub) { measure._nameSub.unsubscribe(); }
    measure._nameSub = this.backendService.getMachineShortNames(payload).subscribe(
      (res: any) => {
        measure._nameLoading = false;
        const raw: any[] = Array.isArray(res?.Data) ? res.Data :
                            Array.isArray(res?.data) ? res.data :
                            Array.isArray(res) ? res : [];
        const newItems: string[] = raw
          .map(item => typeof item === 'string' ? item : (item?.short_name || item?.name || item?.value || ''))
          .filter(Boolean)
          .reverse();
        const crossMeasureNames: string[] = this.dynamicMeasures
          .filter(m => m !== measure && m.short_name?.trim())
          .map(m => m.short_name.trim());
        if (reset) {
          measure._nameItems = [...new Set([...newItems, ...crossMeasureNames])];
        } else {
          const existing: string[] = measure._nameItems || [];
          measure._nameItems = [...new Set([...newItems, ...existing])];
        }
        measure._nameHasMore = newItems.length >= count;
        measure._nameIncreasedCount = (measure._nameIncreasedCount || 0) + count;
      },
      () => { measure._nameLoading = false; }
    );
  }

  onMeasureNameChanged(measure: any, value: any): void {
    const strValue = typeof value === 'string' ? value : (value?.label || value || '');
    measure.short_name = (strValue || '').substring(0, this.measureNameMaxLength);
    this.emitUpdatedMeasures();
    this.measureNameValidationSubject.next();
  }

  onMeasureNameBlur(measure?: any): void {
    if (measure) {
      clearTimeout(measure._nameSearchTimer);
      measure._currentSearch = '';
    }
    this.validateMeasureNames();
    this.emitUpdatedMeasures();
  }

  isControlMeasureType(measure: any): boolean {
    const typeName = measure?.measure_type_name || measure?.selectedMeasureType?.measure_type_name || '';
    const normalizedTypeName = typeName.toString().trim().toLowerCase();
    return normalizedTypeName.includes('control') && normalizedTypeName.includes('measure');
  }

  handleDeterminePlr(measure, index) {
    measure.measurePlrObj.recommendedCategory =  measure?.measurePlrObj?.category
    this.selectedMeasureIndex = index;
    this.determinePlr.emit(measure);
  }

  handleAddCategoryValue() {
    this.addCategoryValue.emit();
  }

  checkFormValidity() {
    this.isFormValid = this.dynamicMeasures.every(
        measure => measure.measure_type_id && measure.control_measure
    );
  }

  addNewMeasureSection(index: number) {
    const newOrderNo = this.dynamicMeasures[index].order_no + 1;
    for (let i = index + 1; i < this.dynamicMeasures.length; i++) {
      this.dynamicMeasures[i].order_no += 1;
    }
    const newMeasure = this.getDefaultMeasure(newOrderNo);
    this.dynamicMeasures.splice(index + 1, 0, newMeasure);
    this.emitUpdatedMeasures?.();
  }

  getDefaultMeasure(orderNo: number) {
    return {
      measure_id: '',
      measure_type_id: '',
      measure_type_name: '',
      short_name: '',
      showWhitespaceOnlyShortNameError: false,
      showDuplicateShortNameError: false,
      control_measure: '',
      order_no: orderNo,
      machineModes: [...this.associatedMachineModes],
      is_plr: false,
      measurePlrObj: {
        plr: '',
        sValue: '',
        pValue: '',
        fValue: '',
        category: '',
        isIncludeRecommandedCategory: false,
        recommendedCategory: '',
        plrDescription: '',
        isPlrFromGraph: false
      }
    };
  }

  private clearMeasureNameErrors(): void {
    this.dynamicMeasures.forEach(measure => {
      measure.showMeasureTypeRequiredError = false;
      measure.showWhitespaceOnlyShortNameError = false;
      measure.showDuplicateShortNameError = false;
    });
  }

  private updateRequiredFieldErrors(): void {
    this.dynamicMeasures.forEach(measure => {
      measure.showMeasureTypeRequiredError = this.showValidationErrors && !measure?.selectedMeasureType && !!measure?.control_measure?.trim();
    });

    if (this.showValidationErrors) {
      this.validateMeasureNames();
    }
  }

  private validateDuplicateMeasureNames(): void {
    this.validateMeasureNames();
  }

  private validateMeasureNames(): void {
    const shortNameCounts = new Map<string, number>();

    this.dynamicMeasures.forEach(measure => {
      const rawShortName = measure?.short_name || '';
      const trimmedShortName = rawShortName.trim();
      const normalizedShortName = trimmedShortName.toLowerCase();

      measure.showWhitespaceOnlyShortNameError = false;
      measure.showDuplicateShortNameError = false;

      if (normalizedShortName) {
        shortNameCounts.set(normalizedShortName, (shortNameCounts.get(normalizedShortName) || 0) + 1);
      }
    });

    this.dynamicMeasures.forEach(measure => {
      const rawShortName = measure?.short_name || '';
      const trimmedShortName = rawShortName.trim();
      const normalizedShortName = trimmedShortName.toLowerCase();
      const isWhitespaceOnly = rawShortName.length > 0 && !trimmedShortName.length;
      const isDuplicate = !!normalizedShortName && (shortNameCounts.get(normalizedShortName) || 0) > 1;

      measure.showDuplicateShortNameError = isDuplicate;
      measure.showWhitespaceOnlyShortNameError = !isDuplicate && isWhitespaceOnly;
    });
  }

  public getMeasuresSnapshot(): any[] {
    this.updateRequiredFieldErrors();
    return this.dynamicMeasures.map((measure) => ({
      ...measure,
      showMeasureTypeRequiredError: undefined,
      showWhitespaceOnlyShortNameError: undefined,
      showDuplicateShortNameError: undefined
    }));
  }

  emitUpdatedMeasures() {
    const measuresToEmit = this.getMeasuresSnapshot();
    console.log('Emitting updated measures:', measuresToEmit);
    this.measuresUpdated.emit(measuresToEmit);
  }

  async deleteMeasure(index: number) {
    const measure = this.dynamicMeasures[index];
    const item = this.translate.instant('measure').toLowerCase();
    const itemName = measure.selectedMeasureType?.label || measure.measure_type_name || '';
    const title = this.translate.instant('Are_you_sure_you_want_to_delete') + ' ' + item + '?';
    const subtitle = itemName ? `'${itemName}'` : '';
    const actionText = this.translate.instant('Delete');
    const isConfirmed = await this.confirmationService.presentConfirmationModal(
      title,
      actionText,
      true,
      this.translate.instant('Cancel'),
      subtitle
    );
    if (isConfirmed) {
      this.dynamicMeasures.splice(index, 1);
      // Reassign correct order_no for remaining items
      this.dynamicMeasures.forEach((item, i) => {
        item.order_no = i + 1;
      });
      this.emitUpdatedMeasures?.();
    }
  }

  toggleIncludeCategory(measure: any, index: any) {
    this.dynamicMeasures[index].measurePlrObj = measure.measurePlrObj;
    this.emitUpdatedMeasures();
  }
}